import { defineStore } from 'pinia'

import * as sellContractApi from '../services/api/sell-contract'
import { IApproval } from './staff'

export interface IInvoiceData {
  note: string
  amount: number
  pay_at: string
}

export interface IAccessory {
  note: string
  price: string
}

export interface ISellContractData {
  id: number
  status: string
  department: string
  is_urgent: boolean
  note: string
  reason: string
  approvals: Array<IApproval>
  sell_sellContract_no: string
  signed_at: string
  source: string
  final_price: number
  brokerage: any
  method: string
  invoices: Array<IInvoiceData>
  received_after_payment: boolean
  received_at: string
  accessories: Array<IAccessory>
  assignee_id: number
  car_id: number
  customer_id: number
  assignee: any
  car: any
  customer: any
  created_at: string
}

export const useSellContractStore = defineStore('sellContract', {
  state: () => ({
    sellContract: {
      id: 0,
      status: '',
      department: '',
      is_urgent: false,
      note: '',
      reason: '',
      approvals: [],
      sell_sellContract_no: '',
      signed_at: '',
      source: '',
      final_price: 0,
      brokerage: {},
      method: '',
      invoices: [],
      received_after_payment: false,
      received_at: '',
      accessories: [],
      assignee_id: 0,
      car_id: 0,
      customer_id: 0,
      assignee: {},
      car: {},
      customer: {},
      created_at: '',
    } as ISellContractData,
    isLoading: false,
  }),

  actions: {
    setSellContract(data: ISellContractData) {
      this.sellContract = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getSellContract(id: number) {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.get(id)
        this.setSellContract(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateSellContract(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.update(this.sellContract.id, payload)
        this.setSellContract(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applySellContract() {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.apply(this.sellContract.id)
        this.setSellContract(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectSellContract(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.reject(this.sellContract.id, payload)
        this.setSellContract(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeSellContract(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.remake(this.sellContract.id, payload)
        this.setSellContract(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveSellContract() {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.approve(this.sellContract.id)
        this.setSellContract(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useSellContractListStore = defineStore('sellContractList', {
  state: () => ({
    sellContracts: [] as ISellContractData[],
    count: 0,
    total: 0,
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addSellContract(data: ISellContractData) {
      this.sellContracts.push(data)
    },

    setSellContracts(data: ISellContractData[]) {
      this.sellContracts = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countSellContracts() {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createSellContract(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await sellContractApi.create(payload)
        this.addSellContract(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listSellContracts(params: any) {
      try {
        this.setLoading(true)
        const result = await sellContractApi.list(params)
        const { data, total } = result as any
        this.setSellContracts(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listSellContractsByCustomer(params: any) {
      try {
        this.setLoading(true)
        const result = await sellContractApi.listByCustomer(params)
        const { data, total } = result as any
        this.setSellContracts(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllSellContracts(params: any) {
      try {
        this.setLoading(true)
        const result = await sellContractApi.listAll(params)
        const { data, total } = result as any
        this.setSellContracts(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
